import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import clientInboxApi from '@src/api/clientInbox';

// Read-only view of GET /client-inbox/autonomy-report/: how often each
// category's drafts go out unchanged, and whether it qualifies for auto-send.

const pct = (share) => (share === null || share === undefined ? '—' : `${Math.round(Number(share) * 100)}%`);
const trendLabel = (trend) => {
  if (trend === null || trend === undefined) return '—';
  const pts = Math.round(Number(trend) * 100);
  return `${pts > 0 ? '+' : ''}${pts} pts`;
};

function AutonomyReportDialog({ open, onClose, days }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    setError('');
    clientInboxApi
      .getAutonomyReport({ days })
      .then((res) => {
        if (active) setReport(res?.data || null);
      })
      .catch((err) => {
        if (active) setError(err?.response?.data?.detail || 'Could not load the autonomy report.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [days, open]);

  const categories = Array.isArray(report?.categories) ? report.categories : [];
  const last14 = report?.last_14_days || null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Autonomy</DialogTitle>
      <DialogContent>
        {loading && !report ? (
          <Box py={3} display="flex" justifyContent="center">
            <CircularProgress size={22} />
          </Box>
        ) : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
        {report ? (
          <>
            <Typography variant="body2" mb={1.5}>
              Mode: <strong>{report.mode || 'off'}</strong>
              {report.window_days ? ` · last ${report.window_days} days` : ''}
            </Typography>
            {categories.length ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Drafts sent</TableCell>
                    <TableCell align="right">% sent unchanged</TableCell>
                    <TableCell align="right">Trend</TableCell>
                    <TableCell align="center">Qualifies</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map((row) => (
                    <TableRow key={row.category}>
                      <TableCell>{String(row.category || '').replace(/_/g, ' ')}</TableCell>
                      <TableCell align="right">{row.n}</TableCell>
                      <TableCell align="right">{pct(row.unchanged_share)}</TableCell>
                      <TableCell align="right">{trendLabel(row.trend)}</TableCell>
                      <TableCell align="center">{row.qualifies ? '✓' : ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No sent drafts in this window.
              </Typography>
            )}
            {last14 ? (
              <Typography variant="body2" color="text.secondary" mt={1.5}>
                Last 14 days: {last14.sends_in_qualifying_categories ?? 0} sends in qualifying categories,{' '}
                {last14.of_which_sent_unchanged ?? 0} sent unchanged, {last14.of_which_edited ?? 0} edited.{' '}
                {last14.threads_evaluated ?? 0} threads evaluated, {last14.threads_eligible ?? 0} eligible.
              </Typography>
            ) : null}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

AutonomyReportDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  days: PropTypes.number,
};
AutonomyReportDialog.defaultProps = { days: 28 };

export default AutonomyReportDialog;
